/*
  'Highest rating' and 'Most reviewed' — two sort orders Shopify does not offer.

  sort_by only accepts Shopify's own keys, so these are applied in the browser.
  Picking one puts sort_by=rating-desc or sort_by=reviews-desc in the URL;
  Shopify does not recognise either value, ignores it and answers with the
  collection's default order, which is exactly what we want to start from. This
  module then pulls every page of that collection through the Section Rendering
  API, reorders the cards by the review figures each one carries in
  data-rating and data-review-count, and pages through the result in place — no
  further requests once it has them.

  A collection of 300 products costs around ten parallel section requests and
  roughly 200KB over the wire, paid once and kept for as long as the page
  lives. Nothing here runs unless the sort is chosen, so an ordinary collection
  view is untouched.
*/
(() => {
  const RESULTS_ID = 'filter-results';
  const PROMO_PREFIX = 'promo-item--';

  /*
    Each sort ranks a card by a list of numbers, compared in turn and always
    highest first. 'Highest rating' falls back to the review count, so five
    stars from forty people outranks five stars from one, and a product with no
    reviews at all sits at the end of either sort on zeroes.
  */
  const SORTS = {
    'reviews-desc': (item) => [reviewCount(item), rating(item)],
    'rating-desc': (item) => [rating(item), reviewCount(item)]
  };

  /**
   * The sort being asked for, if it is one of ours.
   * @returns {string|null}
   */
  function activeSort() {
    const value = new URLSearchParams(window.location.search).get('sort_by');
    return Object.prototype.hasOwnProperty.call(SORTS, value) ? value : null;
  }

  /**
   * The query string this ordering belongs to. Filters change which products
   * are in the collection, so a sorted set is only good for the parameters it
   * was fetched under; page number is not part of that.
   * @returns {string}
   */
  function currentKey() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.sort();
    return params.toString();
  }

  /**
   * The id the Section Rendering API needs for the product grid.
   * @returns {string|null}
   */
  function sectionId() {
    const form = document.getElementById('facets');
    if (form && form.dataset.filterSectionId) return form.dataset.filterSectionId;

    const results = document.getElementById(RESULTS_ID);
    const section = results && results.closest('[id^="shopify-section-"]');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  /**
   * Reads a rendered grid into products and promos.
   *
   * Promo tiles are merchandising blocks rather than results, and the section
   * gives each one a place in the grid: 'top', 'bottom', or the number of the
   * card it sits in front of. Ordering the products cannot be allowed to move
   * them, so each is noted with the slot it occupied and put back there.
   * @param {Document|DocumentFragment} doc - Parsed section markup.
   * @returns {{products: Element[], promos: Array<{node: Element, index: number}>}}
   */
  function readGrid(doc) {
    const grid = doc.querySelector(`#${RESULTS_ID} ul.grid`) || doc.querySelector('ul.grid');
    const products = [];
    const promos = [];
    if (!grid) return { products, promos };

    let index = 0;
    grid.querySelectorAll(':scope > li').forEach((item) => {
      if (item.className.includes(PROMO_PREFIX)) {
        promos.push({ node: item, index });
        index += 1;
      } else if (item.querySelector('product-card')) {
        products.push(item);
        index += 1;
      }
    });

    return { products, promos };
  }

  /**
   * Highest page number the server's own pagination links to.
   * @param {Document} doc - Parsed section markup.
   * @returns {number}
   */
  function pageCount(doc) {
    let last = 1;
    doc.querySelectorAll('.pagination a[href]').forEach((link) => {
      const page = Number(new URL(link.href, window.location.origin).searchParams.get('page'));
      if (page > last) last = page;
    });
    return last;
  }

  class ReviewSort {
    constructor() {
      this.results = document.getElementById(RESULTS_ID);
      this.key = null;
      this.items = [];
      this.promos = [];
      this.pageSize = 30;
      this.page = 1;
      this.prevLabel = 'Previous';
      this.nextLabel = 'Next';

      // The filters rebuild the grid on every change, so the sort has to be
      // reapplied to whatever markup lands there next. The check is deferred a
      // task: facet-filters.js writes the new markup before it updates the URL,
      // and the URL is what says whether this sort is wanted.
      this.observer = new MutationObserver(() => this.queueRun());
      this.observer.observe(this.results, { childList: true, subtree: true });

      window.addEventListener('popstate', () => this.queueRun());
      this.queueRun();
    }

    /**
     * Coalesces the many mutations one filter change makes into a single check.
     */
    queueRun() {
      clearTimeout(this.queued);
      this.queued = setTimeout(() => this.maybeRun(), 0);
    }

    /**
     * Runs the sort if it is wanted and has not already been applied to the
     * markup currently on screen.
     */
    maybeRun() {
      const sort = activeSort();
      if (!sort) return;

      const grid = this.results.querySelector('ul.grid');
      if (!grid || grid.dataset.reviewSorted === currentKey()) return;

      this.markSortSelected(sort);
      this.run(sort);
    }

    /**
     * Shopify never sees this sort value, so it cannot mark the option chosen.
     * Both controls are set here instead, without firing their change events —
     * that would send the filters off on another round trip.
     */
    markSortSelected(sort) {
      document.querySelectorAll(`input[name="sort_by"][value="${sort}"]`).forEach((radio) => {
        radio.checked = true;
      });

      const select = document.getElementById('products-sort-by');
      if (!select) return;

      const option = select.querySelector(`[data-value="${sort}"]`);
      if (!option) return;

      select.querySelectorAll('[aria-selected="true"]').forEach((other) => {
        other.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      const label = select.querySelector('.custom-select__btn span.text-start');
      if (label && option.firstElementChild) label.textContent = option.firstElementChild.textContent;
    }

    /**
     * Fetches the whole collection, orders it and renders the first page.
     */
    async run(sort) {
      const key = currentKey();
      const grid = this.results.querySelector('ul.grid');

      // Claim the grid before the first await, so the mutations this makes
      // cannot start a second run.
      grid.dataset.reviewSorted = key;
      this.results.classList.add('is-loading');

      try {
        const collected = await this.fetchAll();
        if (currentKey() !== key) return;

        this.key = key;
        this.promos = collected.promos;
        this.pageSize = collected.pageSize;

        // Sort is stable, so products the ranking cannot separate keep the
        // order the collection already had them in.
        const rank = SORTS[sort];
        this.items = collected.products.sort((a, b) => compare(rank(a), rank(b)));

        this.render(1);
      } catch (error) {
        // Leave the server's own ordering on screen rather than an empty grid.
        delete grid.dataset.reviewSorted;
        console.error('Review sort failed', error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Pulls every page of the collection. Page one comes first because it is
     * the only one that says how many pages there are; the rest go together.
     * @returns {Promise<object>}
     */
    async fetchAll() {
      const id = sectionId();
      if (!id) throw new Error('No section id for the product grid');

      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.set('section_id', id);

      const url = (page) => `${window.location.pathname}?${params}&page=${page}`;
      const parse = async (response) => {
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      };

      const first = await parse(await fetch(url(1)));
      const total = pageCount(first);
      const firstGrid = readGrid(first);

      const rest = await Promise.all(
        Array.from({ length: total - 1 }, (_, i) => fetch(url(i + 2)).then(parse))
      );

      const products = firstGrid.products.concat(...rest.map((doc) => readGrid(doc).products));

      return {
        products,
        // Only the first page carries promos; the section renders them nowhere else.
        promos: firstGrid.promos,
        // What the collection itself paginates by, taken from a full page.
        pageSize: total > 1 ? firstGrid.products.length : products.length || 1
      };
    }

    /**
     * Renders one page of the sorted set.
     * @param {number} page - 1-based page number.
     */
    render(page) {
      const grid = this.results.querySelector('ul.grid');
      if (!grid) return;

      const pages = Math.max(1, Math.ceil(this.items.length / this.pageSize));
      this.page = Math.min(Math.max(1, page), pages);

      const start = (this.page - 1) * this.pageSize;
      const slice = this.items.slice(start, start + this.pageSize);

      // Promos go back into the slots they were rendered in, ascending so each
      // splice leaves the ones after it where they were. Page one only, which
      // is the only page the section puts them on.
      if (this.page === 1) {
        this.promos.forEach(({ node, index }) => slice.splice(index, 0, node));
      }

      grid.replaceChildren(...slice);
      grid.dataset.reviewSorted = this.key;

      // Card images ship with data-src rather than src and are only given a
      // real source by the theme's lazy loader. Cards parsed out of a section
      // response have never been past it, so without this they stay blank —
      // the same call facet-filters.js makes after it swaps the results in.
      if (typeof window.initLazyImages === 'function') window.initLazyImages();

      this.renderPagination(pages);
    }

    /**
     * Replaces the server's pagination with one that moves through the sorted
     * set in place. Same markup as snippets/pagination.liquid so it inherits
     * the theme's styling, with buttons in place of links — there is no URL to
     * send anyone to.
     * @param {number} pages - Total number of pages.
     */
    renderPagination(pages) {
      this.results.querySelector('.js-review-sort-pagination')?.remove();

      // Borrow the server's own arrow wording before hiding its pagination, so
      // this stays in whatever language the storefront is running in.
      const serverNav = this.results.querySelector('.pagination')?.closest('nav');
      if (serverNav) {
        this.prevLabel = serverNav.querySelector('.pagination__prev-label')?.textContent.trim() || this.prevLabel;
        this.nextLabel = serverNav.querySelector('.pagination__next-label')?.textContent.trim() || this.nextLabel;
        serverNav.hidden = true;
      }

      if (pages < 2) return;

      const nav = document.createElement('nav');
      nav.className = 'js-review-sort-pagination';
      nav.setAttribute('aria-label', 'Pagination');

      const numbers = Array.from({ length: pages }, (_, i) => i + 1)
        .map((page) => (page === this.page
          ? `<li class="hidden md:block"><span class="pagination__page-current font-bold block leading-none">${page}</span></li>`
          : `<li class="hidden md:block"><button type="button" class="pagination__page-link text-current block leading-none" data-page="${page}">${page}</button></li>`))
        .join('');

      nav.innerHTML = `
        <ul class="pagination relative flex flex-wrap justify-center items-center justify-between w-full mx-auto mb-10">
          <li class="pagination__item pagination__item--arrow">
            <button type="button" class="pagination__arrow pagination__arrow--prev hover:lighten flex text-current items-center has-ltr-icon" data-page="${this.page - 1}"${this.page === 1 ? ' disabled' : ''}>
              <span class="pagination__prev-label mis-2">${this.prevLabel}</span>
            </button>
          </li>
          <li class="pagination__item text-center md:hidden font-bold">${this.page} / ${pages}</li>
          ${numbers}
          <li class="pagination__item pagination__item--arrow">
            <button type="button" class="pagination__arrow pagination__arrow--next hover:lighten flex justify-end text-current items-center has-ltr-icon" data-page="${this.page + 1}"${this.page === pages ? ' disabled' : ''}>
              <span class="pagination__next-label text-end">${this.nextLabel}</span>
            </button>
          </li>
        </ul>
      `;

      nav.addEventListener('click', (evt) => {
        const button = evt.target.closest('button[data-page]');
        if (!button || button.disabled) return;

        this.render(Number(button.dataset.page));
        this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.results.appendChild(nav);
    }
  }

  /**
   * A number a card was rendered with, or zero when it carries none.
   * @param {Element} item - Grid <li>.
   * @param {string} name - Dataset key on the product-card.
   * @returns {number}
   */
  function figure(item, name) {
    const card = item.querySelector('product-card');
    return Number(card && card.dataset[name]) || 0;
  }

  /**
   * @param {Element} item - Grid <li>.
   * @returns {number} Number of reviews the card was rendered with.
   */
  function reviewCount(item) {
    return figure(item, 'reviewCount');
  }

  /**
   * @param {Element} item - Grid <li>.
   * @returns {number} Average rating the card was rendered with.
   */
  function rating(item) {
    return figure(item, 'rating');
  }

  /**
   * Compares two ranking lists, highest first, each number breaking the tie
   * left by the one before it.
   * @param {number[]} a - First card's ranking.
   * @param {number[]} b - Second card's ranking.
   * @returns {number}
   */
  function compare(a, b) {
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return b[i] - a[i];
    }
    return 0;
  }

  function init() {
    if (document.getElementById(RESULTS_ID)) new ReviewSort();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
